import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Box, Typography, Paper, Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

// Styles personnalisés
const UploadBox = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  border: `2px dashed ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.background.default,
  cursor: 'pointer',
  transition: 'border .3s ease-in-out',
  minHeight: 200,
  '&:hover': {
    borderColor: theme.palette.primary.dark,
  },
}));

const AudioUploader = ({ onFileSelect, maxSizeMB = 900 }) => {
  const [error, setError] = useState('');
  
  // Fonction pour gérer le drop de fichier (SUPPORT MULTI-FICHIERS)
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setError('');
    
    // Vérifier les fichiers rejetés
    if (rejectedFiles && rejectedFiles.length > 0) {
      rejectedFiles.forEach(rejectedFile => {
        rejectedFile.errors.forEach(err => {
          if (err.code === 'file-too-large') {
            setError(`Le fichier "${rejectedFile.file.name}" est trop volumineux (max: ${maxSizeMB}MB)`);
          } else if (err.code === 'file-invalid-type') {
            setError(`Le format du fichier "${rejectedFile.file.name}" n'est pas supporté`);
          } else {
            setError(`Erreur avec le fichier "${rejectedFile.file.name}": ${err.message}`);
          }
        });
      });
      return;
    }
    
    // Traiter tous les fichiers acceptés
    if (acceptedFiles && acceptedFiles.length > 0) {
      if (acceptedFiles.length === 1) {
        // Un seul fichier : comportement standard
        onFileSelect(acceptedFiles[0]);
      } else {
        // Plusieurs fichiers : envoyer le tableau complet en une fois
        onFileSelect(acceptedFiles);
      }
    }
  }, [maxSizeMB, onFileSelect]);
  
  // Configuration de react-dropzone (MULTI-FICHIERS ACTIVÉ)
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.flac', '.aac'],
      'video/*': ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv']
    },
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: true  // ✅ MULTI-FICHIERS ACTIVÉ
  });
  
  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Zone de drop pour multi-fichiers */}
      <UploadBox {...getRootProps()}>
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        {isDragActive ? (
          <Typography variant="h6" color="primary" gutterBottom>
            Relâchez pour ajouter les fichiers audio/vidéo
          </Typography>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Glissez vos fichiers audio/vidéo ici
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              ou cliquez pour sélectionner des fichiers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Formats supportés: MP3, WAV, M4A, FLAC, AAC, MP4, AVI, MOV, WMV, FLV, WebM, MKV (max {maxSizeMB}MB par fichier)
            </Typography>
          </>
        )}
      </UploadBox>
    </Box>
  );
};

export default AudioUploader;